import type { Note } from '@voice/shared';

interface ConfidenceBannerProps {
  note: Note;
  onUndo: () => void;
  onReviewClick: () => void;
}

export function ConfidenceBanner({ note, onUndo, onReviewClick }: ConfidenceBannerProps) {
  const { confidence } = note;

  // Legacy notes (no confidence) — no banner
  if (confidence == null) return null;

  // High confidence (>= 0.80)
  if (confidence >= 0.80) {
    return (
      <div style={styles.bannerHigh}>
        <span style={styles.bannerIcon}>&#10003;</span>
        <span style={styles.bannerText}>AI processed this note</span>
        <button style={styles.undoBtn} onClick={onUndo} type="button">
          Undo
        </button>
      </div>
    );
  }

  // Medium confidence (0.60-0.79)
  if (confidence >= 0.60) {
    return (
      <div style={styles.bannerMedium}>
        <span style={styles.bannerIcon}>&#9888;</span>
        <span style={styles.bannerText}>Review AI results</span>
        <button style={styles.reviewBtn} onClick={onReviewClick} type="button">
          Review
        </button>
      </div>
    );
  }

  // Low confidence (< 0.60)
  return (
    <div style={styles.bannerLow}>
      <span style={styles.bannerIcon}>&#63;</span>
      <span style={styles.bannerText}>AI could not confidently classify this note</span>
    </div>
  );
}

const styles = {
  bannerHigh: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-2) var(--space-3)',
    backgroundColor: 'rgba(34, 197, 94, 0.1)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid rgba(34, 197, 94, 0.2)',
  },
  bannerMedium: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-2) var(--space-3)',
    backgroundColor: 'rgba(245, 158, 11, 0.1)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid rgba(245, 158, 11, 0.2)',
  },
  bannerLow: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-2) var(--space-3)',
    backgroundColor: 'var(--color-surface)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--color-border)',
  },
  bannerIcon: {
    fontSize: 'var(--font-size-sm)',
    flexShrink: 0,
  },
  bannerText: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-secondary)',
    flex: 1,
  },
  undoBtn: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-text-muted)',
    padding: '2px 8px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--color-border)',
    background: 'none',
    cursor: 'pointer',
  },
  reviewBtn: {
    fontSize: 'var(--font-size-xs)',
    color: 'var(--color-warning)',
    padding: '2px 8px',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid rgba(245, 158, 11, 0.3)',
    background: 'none',
    cursor: 'pointer',
    fontWeight: 600,
  },
};
